import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./Input.css";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  successText?: ReactNode;
  invalid?: boolean;
  valid?: boolean;
  size?: "sm" | "md" | "lg";
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    errorText,
    successText,
    invalid,
    valid,
    size = "md",
    id,
    className,
    disabled,
    "aria-describedby": ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? `go4-input-${autoId}`;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const successId = `${inputId}-success`;
  const isInvalid = invalid || Boolean(errorText);

  const describedBy =
    [
      ariaDescribedBy,
      errorText ? errorId : null,
      successText && !errorText ? successId : null,
      helperText && !errorText && !successText ? helperId : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={cx("go4-field", disabled && "go4-field--disabled", className)}>
      {label ? (
        <label
          htmlFor={inputId}
          className={cx("go4-field__label", isInvalid && "go4-field__label--invalid")}
        >
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        disabled={disabled}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        className={cx(
          "go4-input",
          `go4-input--${size}`,
          isInvalid && "go4-input--invalid",
          !isInvalid && valid && "go4-input--valid",
        )}
        {...rest}
      />
      {errorText ? (
        <p id={errorId} className="go4-field__error" role="alert">
          {errorText}
        </p>
      ) : successText ? (
        <p id={successId} className="go4-field__success">
          {successText}
        </p>
      ) : helperText ? (
        <p id={helperId} className="go4-field__helper">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
